import * as React from "react";
import { Menu as MenuPrimitive } from "@base-ui/react/menu";

import { cn } from "../../lib/utils";
import { IconChevronRight, IconCheck } from "@tabler/icons-react";

type DropdownMenuSize = "sm" | "default" | "lg";

const dropdownMenuSizeStyles: Record<DropdownMenuSize, string> = {
  sm: "p-0.5 [&_[data-slot$='-menu-label']]:px-1 [&_[data-slot$='-menu-label']]:py-0.5 [&_[data-slot$='-menu-label']]:text-caption [&_[data-slot$='-menu-label']]:leading-caption [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:gap-1 [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:px-1 [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:py-0.5 [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:text-nav [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:leading-nav [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])_svg:not([class*='size-'])]:size-3.5 [&_[data-slot$='-menu-separator']]:mx-1 [&_[data-slot$='-menu-separator']]:my-0.5 [&_[data-slot$='-menu-shortcut']]:text-caption [&_[data-slot$='-menu-shortcut']]:leading-caption",
  default: "",
  lg: "p-2 [&_[data-slot$='-menu-label']]:px-2 [&_[data-slot$='-menu-label']]:py-1.5 [&_[data-slot$='-menu-label']]:text-sm [&_[data-slot$='-menu-label']]:leading-sm [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:gap-2 [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:px-2 [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:py-1.5 [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:text-md [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])]:leading-md [&_:is([data-slot$='-menu-item'],[data-slot$='-menu-sub-trigger'],[data-slot$='-menu-checkbox-item'],[data-slot$='-menu-radio-item'])_svg:not([class*='size-'])]:size-4.5 [&_[data-slot$='-menu-separator']]:mx-2 [&_[data-slot$='-menu-separator']]:my-1.5 [&_[data-slot$='-menu-shortcut']]:text-xs [&_[data-slot$='-menu-shortcut']]:leading-xs",
};

const dropdownMenuStyles = {
  trigger: "select-none",
  positioner: "z-floating isolate outline-none",
  content:
    "z-floating max-h-[var(--available-height)] min-w-32 origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-lg border border-border bg-floating p-1 text-floating-fg shadow-none outline-none transition-[scale,opacity,translate] duration-150 ease-in-out will-change-transform data-[closed]:overflow-hidden data-ending-style:scale-95 data-starting-style:scale-95 data-ending-style:opacity-0 data-starting-style:opacity-0 data-[side=bottom]:data-ending-style:-translate-y-2 data-[side=bottom]:data-starting-style:-translate-y-2 data-[side=top]:data-ending-style:translate-y-2 data-[side=top]:data-starting-style:translate-y-2 data-[side=left]:data-ending-style:translate-x-2 data-[side=left]:data-starting-style:translate-x-2 data-[side=inline-start]:data-ending-style:translate-x-2 data-[side=inline-start]:data-starting-style:translate-x-2 data-[side=right]:data-ending-style:-translate-x-2 data-[side=right]:data-starting-style:-translate-x-2 data-[side=inline-end]:data-ending-style:-translate-x-2 data-[side=inline-end]:data-starting-style:-translate-x-2 focus-visible:shadow-none focus-visible:outline-none",
  label:
    "px-1.5 py-1 text-xs font-medium leading-xs text-fg-muted data-[inset]:pl-7",
  item: "relative flex cursor-default select-none items-center gap-1.5 rounded-md px-1.5 py-1 text-sm leading-sm outline-none data-[disabled]:opacity-50 data-[inset]:pl-7 data-[variant=destructive]:text-destructive data-[focus]:bg-accent data-[focus]:text-accent-foreground data-[variant=destructive]:data-[focus]:bg-[color-mix(in_srgb,var(--colors-destructive)_10%,transparent)] data-[variant=destructive]:data-[focus]:text-destructive focus:bg-accent focus:text-accent-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 dark:data-[variant=destructive]:data-[focus]:bg-[color-mix(in_srgb,var(--colors-destructive)_20%,transparent)]",
  subTrigger:
    "relative flex cursor-default select-none items-center gap-1.5 rounded-md px-1.5 py-1 text-sm leading-sm outline-none data-[disabled]:opacity-50 data-[inset]:pl-7 data-[open]:bg-accent data-[open]:text-accent-foreground data-[focus]:bg-accent data-[focus]:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  subContent: "w-auto min-w-24",
  checkboxItem:
    "relative flex cursor-default select-none items-center gap-1.5 rounded-md py-1 pl-1.5 pr-8 text-sm leading-sm outline-none data-[disabled]:opacity-50 data-[inset]:pl-7 data-[focus]:bg-accent data-[focus]:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  radioItem:
    "relative flex cursor-default select-none items-center gap-1.5 rounded-md py-1 pl-1.5 pr-8 text-sm leading-sm outline-none data-[disabled]:opacity-50 data-[inset]:pl-7 data-[focus]:bg-accent data-[focus]:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  itemIndicator:
    "pointer-events-none absolute right-2 flex items-center justify-center",
  separator: "mx-1.5 my-1 h-px bg-border",
  shortcut:
    "ml-auto text-xs leading-xs tracking-wide text-fg-muted [data-slot$='-menu-item']:focus_&:text-accent-foreground",
  chevron: "ml-auto",
};

const dropdownMenuItemSelector = [
  "[data-slot='dropdown-menu-item']",
  "[data-slot='dropdown-menu-sub-trigger']",
  "[data-slot='dropdown-menu-checkbox-item']",
  "[data-slot='dropdown-menu-radio-item']",
].join(",");

function focusNextDropdownMenuItem(
  menu: HTMLElement,
  currentTarget: EventTarget | null,
  backwards: boolean,
) {
  const items = Array.from(
    menu.querySelectorAll<HTMLElement>(dropdownMenuItemSelector),
  ).filter(
    (item) =>
      !item.hasAttribute("data-disabled") &&
      item.getAttribute("aria-disabled") !== "true",
  );

  if (items.length === 0) return;

  const currentIndex = items.findIndex((item) => item === currentTarget);
  const fallbackIndex = backwards ? 0 : -1;
  const nextIndex =
    (currentIndex === -1 ? fallbackIndex : currentIndex) + (backwards ? -1 : 1);
  items.at(nextIndex % items.length)?.focus();
}

function DropdownMenu({ ...props }: MenuPrimitive.Root.Props) {
  return <MenuPrimitive.Root data-slot="dropdown-menu" {...props} />;
}

function DropdownMenuPortal({ ...props }: MenuPrimitive.Portal.Props) {
  return <MenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />;
}

function DropdownMenuTrigger({ ...props }: MenuPrimitive.Trigger.Props) {
  return <MenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />;
}

function DropdownMenuContent({
  size = "default",
  align = "start",
  alignOffset = 0,
  side = "bottom",
  sideOffset = 4,
  className,
  onKeyDown,
  ...props
}: Omit<MenuPrimitive.Popup.Props, "size"> & {
  size?: DropdownMenuSize;
} & Pick<
    MenuPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset"
  >) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner
        className={dropdownMenuStyles.positioner}
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
      >
        <MenuPrimitive.Popup
          data-slot="dropdown-menu-content"
          onKeyDown={(event) => {
            onKeyDown?.(event);
            if (event.defaultPrevented || event.key !== "Tab") return;

            event.preventDefault();
            focusNextDropdownMenuItem(
              event.currentTarget,
              event.target,
              event.shiftKey,
            );
          }}
          className={cn(
            dropdownMenuStyles.content,
            dropdownMenuSizeStyles[size],
            className,
          )}
          {...props}
        />
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  );
}

function DropdownMenuGroup({ ...props }: MenuPrimitive.Group.Props) {
  return <MenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />;
}

function DropdownMenuLabel({
  className,
  inset,
  ...props
}: MenuPrimitive.GroupLabel.Props & {
  inset?: boolean;
}) {
  return (
    <MenuPrimitive.GroupLabel
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn(dropdownMenuStyles.label, className)}
      {...props}
    />
  );
}

function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  ...props
}: MenuPrimitive.Item.Props & {
  inset?: boolean;
  variant?: "default" | "destructive";
}) {
  return (
    <MenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(dropdownMenuStyles.item, className)}
      {...props}
    />
  );
}

function DropdownMenuSub({ ...props }: MenuPrimitive.SubmenuRoot.Props) {
  return <MenuPrimitive.SubmenuRoot data-slot="dropdown-menu-sub" {...props} />;
}

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: MenuPrimitive.SubmenuTrigger.Props & {
  inset?: boolean;
}) {
  return (
    <MenuPrimitive.SubmenuTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(dropdownMenuStyles.subTrigger, className)}
      {...props}
    >
      {children}
      <IconChevronRight className={dropdownMenuStyles.chevron} />
    </MenuPrimitive.SubmenuTrigger>
  );
}

function DropdownMenuSubContent({
  size = "default",
  align = "start",
  alignOffset = -3,
  side = "right",
  sideOffset = 0,
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuContent>) {
  return (
    <DropdownMenuContent
      data-slot="dropdown-menu-sub-content"
      size={size}
      className={cn(dropdownMenuStyles.subContent, className)}
      align={align}
      alignOffset={alignOffset}
      side={side}
      sideOffset={sideOffset}
      {...props}
    />
  );
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  inset,
  ...props
}: MenuPrimitive.CheckboxItem.Props & {
  inset?: boolean;
}) {
  return (
    <MenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      data-inset={inset}
      className={cn(dropdownMenuStyles.checkboxItem, className)}
      checked={checked}
      {...props}
    >
      <span
        className={dropdownMenuStyles.itemIndicator}
        data-slot="dropdown-menu-checkbox-item-indicator"
      >
        <MenuPrimitive.CheckboxItemIndicator>
          <IconCheck />
        </MenuPrimitive.CheckboxItemIndicator>
      </span>
      {children}
    </MenuPrimitive.CheckboxItem>
  );
}

function DropdownMenuRadioGroup({ ...props }: MenuPrimitive.RadioGroup.Props) {
  return (
    <MenuPrimitive.RadioGroup
      data-slot="dropdown-menu-radio-group"
      {...props}
    />
  );
}

function DropdownMenuRadioItem({
  className,
  children,
  inset,
  ...props
}: MenuPrimitive.RadioItem.Props & {
  inset?: boolean;
}) {
  return (
    <MenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      data-inset={inset}
      className={cn(dropdownMenuStyles.radioItem, className)}
      {...props}
    >
      <span
        className={dropdownMenuStyles.itemIndicator}
        data-slot="dropdown-menu-radio-item-indicator"
      >
        <MenuPrimitive.RadioItemIndicator>
          <IconCheck />
        </MenuPrimitive.RadioItemIndicator>
      </span>
      {children}
    </MenuPrimitive.RadioItem>
  );
}

function DropdownMenuSeparator({
  className,
  ...props
}: MenuPrimitive.Separator.Props) {
  return (
    <MenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn(dropdownMenuStyles.separator, className)}
      {...props}
    />
  );
}

function DropdownMenuShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(dropdownMenuStyles.shortcut, className)}
      {...props}
    />
  );
}

export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
};
